// FUNCTIONALITY-102 — Store V2 customer/session/event capture (marketing).
// Same-origin, fire-and-forget: browser -> /api/store-v2/event (Pages Function) -> Worker D1.
// Never blocks UI, never throws, never stores card data. Phone is never sent here.
import {
  getObservabilityNavigationId,
  getObservabilitySessionId,
  getObservabilitySnapshot,
  recordLastApiError,
  recordLastError,
  recordObservabilityAction,
  setObservabilityBuildId,
} from "./observability-ring-v2.js";

export type StoreV2EventType =
  | "app_open"
  | "consent_accept"
  | "category_view"
  /** CUSTOMER-R4.9 — category load telemetry (no PII; category_key only). */
  | "category_load_started"
  | "category_first_content"
  | "category_loaded"
  | "category_products_painted"
  | "category_load_failed"
  | "search"
  | "search_performed"
  | "search_started"
  | "search_query_submitted"
  | "search_exact_results"
  | "search_typo_corrected"
  | "search_typo_suggestion"
  | "search_zero_results"
  | "search_no_results"
  | "search_results_shown"
  | "search_failed"
  | "search_retry_clicked"
  | "search_scope_resolved"
  | "search_result_clicked"
  | "search_add_to_cart"
  | "search_purchase_attributed"
  | "search_identifier_match"
  | "search_reformulated"
  | "search_alias_candidate_created"
  | "search_alternatives_shown"
  | "search_alternative_click"
  | "route_load_started"
  | "route_load_completed"
  | "route_load_failed"
  | "product_view"
  | "customer_route_unresolved"
  | "add_to_cart"
  | "cart_qty_change"
  | "cart_recovery_detected"
  | "cart_recovery_viewed"
  | "cart_recovery_resumed"
  | "checkout_recovery_detected"
  | "checkout_recovery_viewed"
  | "checkout_recovery_resumed"
  | "promo_apply"
  | "promo_reject"
  | "checkout_start"
  | "order_created"
  | "customer_data_delete_requested"
  // FUNCTIONALITY-104 — customer tapped "Зв'язатися з менеджером".
  | "manager_contact_requested"
  | "contact_call_clicked"
  | "contact_phone_clicked"
  | "contact_viber_clicked"
  | "contact_telegram_clicked"
  | "contact_email_clicked"
  | "contact_map_clicked"
  | "contact_instagram_clicked"
  | "contact_action_fallback_used"
  | "contact_fallback_shown"
  | "contact_modal_opened"
  | "contact_modal_closed"
  | "availability_requested"
  | "dropshipping_lead_requested"
  | "payment_help_requested"
  | "payment_novapay_create_requested"
  | "payment_novapay_create_ok"
  | "payment_novapay_create_failed"
  | "payment_novapay_not_live"
  | "payment_novapay_timing"
  | "payment_novapay_status_poll"
  | "payment_novapay_abandoned"
  | "payment_novapay_back_to_order"
  | "payment_novapay_opened"
  | "customer_marketing_suppressed"
  | "customer_marketing_granted"
  | "marketing_permission_granted_entry"
  | "marketing_permission_not_granted_entry"
  | "legal_gate_views"
  | "marketing_option_views"
  | "marketing_optin_home_shown"
  | "marketing_optin_home_granted"
  | "marketing_optin_home_dismissed"
  | "store_entries"
  | "home_banner_clicked"
  | "payment_receipt_upload_requested"
  | "payment_receipt_picker_opened"
  | "payment_receipt_uploaded"
  | "payment_receipt_upload_failed"
  | "payment_receipt_upload_unavailable"
  | "document_deeplink_opened"
  | "referral_link_created"
  | "referral_share_clicked"
  | "referral_share_open_attempted"
  | "referral_share_fallback_shown"
  | "referral_share_sent"
  | "referral_share_failed"
  | "referral_share_error"
  | "referral_link_copied"
  | "referral_card_viewed"
  | "referral_card_impression"
  | "referral_share_started"
  | "referral_share_completed"
  | "referral_rules_opened"
  | "referral_exit_rescue_shown"
  | "referral_exit_rescue_dismissed"
  | "referral_exit_rescue_shared"
  | "referral_order_attributed"
  | "rules_opened"
  | "crosssell_view"
  | "crosssell_add"
  | "crosssell_skip"
  | "crosssell_added_to_order"
  | "recipe_sent_planned"
  | "cart_clear_requested"
  | "cart_cleared"
  | "cart_clear_cancelled"
  /** MERCH-129A-R1 — compact cart icon opens cart (navigation only, not add). */
  | "cart_opened_from_product_card"
  | "product_added"
  | "add_to_cart_to_cart_latency"
  | "brand_opened"
  | "country_opened"
  | "filter_applied"
  /** SURFACES-UI-001-R1 — global × close (no PII). */
  | "surface_close_clicked"
  // STAGING-LAUNCH-READINESS-002-R2 — customer cabinet ("Мій кабінет").
  | "cabinet_opened"
  | "orders_list_opened"
  | "order_detail_opened"
  | "receipt_section_viewed"
  | "receipt_link_opened"
  | "repeat_order_clicked"
  | "repeat_order_added"
  | "reorder_clicked"
  | "reorder_snapshot_loaded"
  | "reorder_item_available"
  | "reorder_item_unavailable"
  | "reorder_cart_merged"
  | "reorder_checkout_opened"
  | "reorder_order_created"
  | "reorder_abandoned"
  | "reorder_paid"
  | "saved_cart_opened"
  | "invite_friends_opened"
  // STAGING-LAUNCH-READINESS-002-R2-R2 — customer self-service order cancel.
  | "order_cancelled_by_customer"
  | "payment_method_changed_by_customer"
  | "payment_method_selector_opened"
  | "payment_method_selected"
  | "payment_method_change_submitted"
  | "payment_method_change_succeeded"
  | "payment_method_change_failed"
  | "payment_method_selector_empty"
  | "payment_abandoned_by_customer"
  | "continue_payment_clicked"
  | "payment_reminder_open_order"
  | "payment_reminder_continue_payment"
  // STAGING-LAUNCH-READINESS-002-R2 — exit retention sheet.
  | "exit_attempt"
  | "exit_sheet_shown"
  | "continue_shopping"
  | "open_cart"
  | "close_confirmed"
  | "referral_opened"
  | "recent_products_opened"
  // STAGING-LAUNCH-READINESS-002-R2-R2 — sanitized perf telemetry (no PII/tokens/URLs;
  // `route` is always a generic view name like "orders"/"category", never an id/query).
  | "perf_route_stage"
  // BANK-IBAN-R3.4 — non-sensitive bank-transfer UX (never tokens, URLs, IBAN, purpose, payer PII).
  | "bank_checkout_cta_clicked"
  | "bank_instruction_created"
  | "bank_deeplink_attempted"
  | "bank_deeplink_fallback_shown"
  | "bank_field_copied"
  | "bank_payment_page_opened"
  | "bank_returned_to_miniapp"
  | "bank_confirmed"
  | "bank_launcher_open_clicked"
  | "bank_launcher_provider_selected"
  | "bank_bridge_rendered"
  | "bank_deeplink_dispatched"
  | "page_became_hidden"
  | "page_returned_visible"
  | "bank_launch_retry"
  | "bank_launch_fallback"
  | "bank_payment_confirmed"
  | "cart_recommendation_impression"
  | "cart_recommendation_refresh"
  | "cart_recommendation_add"
  | "checkout_recommendation_impression"
  | "checkout_recommendation_click"
  | "checkout_recommendation_add"
  | "recommendation_impression"
  | "recommendation_refresh"
  | "recommendation_click"
  | "recommendation_add_to_cart"
  | "recommendation_add_attempt"
  | "recommendation_add_success"
  | "recommendation_add_failure"
  | "category_viewed"
  | "category_sort_changed"
  | "search_started"
  | "search_suggestion_clicked"
  | "search_results_viewed"
  | "product_viewed"
  | "recommendation_impression"
  | "recommendation_click"
  | "recommendation_add_to_cart"
  | "shipping_branch_search"
  | "shipping_branch_selected"
  | "checkout_payment_timing_selected"
  | "checkout_pay_bank_action_clicked"
  | "checkout_confirm_availability_action_clicked"
  | "order_create_started"
  | "order_create_succeeded"
  | "order_create_failed"
  | "bank_app_open_attempted"
  | "bank_app_open_succeeded"
  | "bank_app_open_fallback"
  | "bank_requisites_copy_all"
  | "referral_open"
  | "referral_variant_selected"
  | "referral_deeplink_opened"
  | "referred_user_first_open"
  | "referred_user_consent"
  | "referred_user_first_order"
  | "referred_user_paid_order"
  | "page_view"
  | "page_leave"
  | "navigation_click"
  | "customer_route_open"
  | "customer_route_error"
  | "customer_internal_navigation"
  | "telegram_back_used"
  | "logical_parent_fallback"
  | "unexpected_external_exit_attempt"
  | "cta_click"
  | "category_open"
  | "brand_open"
  | "country_open"
  | "product_open"
  | "search_submit"
  | "search_result_click"
  | "checkout_started"
  | "checkout_action_selected"
  | "feedback_opened"
  | "feedback_submitted"
  | "api_error"
  | "customer_boot_completed"
  | "customer_boot_failed"
  | "production_build_attestation"
  | "scroll_depth";

const EVENT_URL = "/api/store-v2/event";

interface TelegramContext {
  telegramUserId: string | null;
  startParam: string | null;
}

function readTelegramContext(): TelegramContext {
  try {
    const wa = (window as unknown as { Telegram?: { WebApp?: { initDataUnsafe?: Record<string, unknown> } } })
      .Telegram?.WebApp;
    const unsafe = wa?.initDataUnsafe ?? {};
    const user = (unsafe.user ?? {}) as { id?: number | string };
    const telegramUserId = user.id != null ? String(user.id).slice(0, 32) : null;
    const startParam = typeof unsafe.start_param === "string" ? unsafe.start_param.slice(0, 64) : null;
    return { telegramUserId, startParam };
  } catch {
    return { telegramUserId: null, startParam: null };
  }
}

interface EmitContext {
  sessionKey: string;
  buildId: string;
}

let ctx: EmitContext | null = null;

/** Called once by the app shell so every event carries session + build. */
export function initEvents(next: EmitContext): void {
  ctx = next;
  setObservabilityBuildId(next.buildId);
}

function sanitizeEventPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (/(token|uri|url|iban|purpose|payer|clipboard|initData|rnokpp)/i.test(key)) continue;
    if (typeof value === "string" && /\/pay\/|bank\.gov\.ua\/qr\/|privat24/i.test(value)) continue;
    out[key] = value;
  }
  return out;
}

/** Fire-and-forget event capture. Safe to call from render handlers. */
export function emitEvent(type: StoreV2EventType, payload: Record<string, unknown> = {}): void {
  if (!ctx) return;
  recordObservabilityAction(type);
  if (type === "api_error") {
    recordLastApiError(
      typeof payload.code === "string" ? payload.code : null,
      typeof payload.path === "string" ? payload.path : null,
    );
  }
  if (type === "route_load_failed" || type === "customer_boot_failed") {
    recordLastError(
      typeof payload.code === "string" ? payload.code : type,
      typeof payload.message === "string" ? payload.message : null,
    );
  }
  const tg = readTelegramContext();
  const snap = getObservabilitySnapshot();
  const body = {
    event_type: type,
    session_key: ctx.sessionKey,
    build_id: ctx.buildId,
    telegram_user_id: tg.telegramUserId,
    start_param: tg.startParam,
    surface: "store_v2",
    properties: sanitizeEventPayload({
      route: snap.route,
      source_route: snap.source_route,
      session_id: getObservabilitySessionId(),
      navigation_id: getObservabilityNavigationId(),
      public_order_number: snap.public_order_number,
      ...payload,
    }),
  };
  try {
    const json = JSON.stringify(body);
    // keepalive lets order_created flush even as the success view navigates.
    void fetch(EVENT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: json,
      keepalive: true,
    }).catch(() => {
      /* analytics is best-effort */
    });
  } catch {
    /* never break UX for analytics */
  }
}
